// Small, fast versions of our uploaded images.
//
// On the live site, Cloudflare Image Transformations can resize an image on the
// fly through  https://<site>/cdn-cgi/image/<options>/<image url>  (and cache the
// result). Everything here is a safe no-op unless that can work:
//   - only on the live site's domain (never on localhost or *.pages.dev),
//   - only for images stored on our own storage, on a host Cloudflare is allowed
//     to resize from (see RESIZE_HOSTS),
//   - and it switches itself off for the rest of the visit if resized images
//     keep failing (for example, if the setting isn't switched on in Cloudflare).
// Callers always fall back to the original image if a resized one fails to load.

const FORCE = process.env.REACT_APP_IMAGE_RESIZE === "on"; // for testing away from the live domain
const RESIZE_HOSTS = (process.env.REACT_APP_RESIZE_HOSTS || "api.alphaaudios.xyz")
  .split(",")
  .map((h) => h.trim())
  .filter(Boolean);

// Only ever ask for these widths, so the number of distinct images Cloudflare
// has to make (the free plan covers 5,000 a month) stays small.
const WIDTHS = [96, 192, 320, 480, 640, 960, 1280, 1600];

let failures = 0;

const onLiveSite = () =>
  typeof window !== "undefined" && /(^|\.)alphaaudios\.xyz$/.test(window.location.hostname);

export function resizeEnabled() {
  return failures < 3 && (FORCE || onLiveSite());
}

/** Call when a resized image failed to load; after 3 failures resizing is skipped. */
export function noteResizeFailure() {
  failures += 1;
}

/**
 * Returns the URL of a small copy of `src`, at least `width` CSS-pixel-equivalents
 * wide (pass 2x the size it is shown at for sharp screens). Returns `src`
 * unchanged whenever resizing doesn't apply. `still` turns animated images
 * (GIF avatars) into a single frame, for tiny spots.
 */
export function resizedUrl(src, width, { still = false } = {}) {
  if (!src || typeof src !== "string" || !resizeEnabled()) return src;
  let u;
  try {
    u = new URL(src);
  } catch {
    return src;
  }
  if (!RESIZE_HOSTS.includes(u.hostname)) return src;
  if (!u.pathname.startsWith("/api/files/")) return src;
  if (!/\.(png|jpe?g|webp|gif|avif)$/i.test(u.pathname)) return src;

  const w = WIDTHS.find((x) => x >= width) || WIDTHS[WIDTHS.length - 1];
  const options = [`width=${w}`, "fit=scale-down", "quality=80", "format=auto", still ? "anim=false" : null, "onerror=redirect"]
    .filter(Boolean)
    .join(",");
  return `${window.location.origin}/cdn-cgi/image/${options}/${src}`;
}

/** A CSS `url(...)` of a small copy of the image, for `style={{ backgroundImage }}`. */
export function bgUrl(src, width) {
  return src ? `url("${resizedUrl(src, width)}")` : undefined;
}
